export type OperationsCallStatus =
  | 'pending'
  | 'recording'
  | 'transcribing'
  | 'awaiting_summary'
  | 'summarizing'
  | 'complete'
  | 'failed'

export interface OperationsDashboardTodo {
  task?: string | null
  assignee?: 'manager' | 'liver' | string | null
  completed?: boolean
  due_date?: string | null
  confidence?: number | null
  evidence?: string | null
}

export interface OperationsDashboardCall {
  id: string
  liver_id: string
  called_at: string
  duration_seconds: number | null
  status: OperationsCallStatus
  todos: OperationsDashboardTodo[] | null
  next_follow_date: string | null
  summary?: string | null
  error_message?: string | null
  updated_at?: string | null
  audio_storage_path?: string | null
  transcript?: string | null
}

export interface OperationsDashboardLiver {
  id: string
  display_name?: string | null
}

export interface OperationsDashboardMetrics {
  monthlyCallCount: number
  monthlyDurationSeconds: number
  failedCallCount: number
  stuckCallCount: number
  openTodoCount: number
  overdueTodoCount: number
  overdueNextFollowCount: number
  dormantLiverCount: number
}

export interface OperationsNextAction {
  tone: OperationsHomeItemTone
  title: string
  description: string
  primaryLabel: string
}

export type OperationsStatusCounts = Record<OperationsCallStatus, number>

export interface OperationsReportLiverRow {
  liverId: string
  liverName: string
  callCount: number
  completeCallCount: number
  failedCallCount: number
  durationSeconds: number
  openTodoCount: number
  overdueTodoCount: number
  lastCallAt: string | null
}

export interface OperationsReportMonthPoint {
  month: string
  callCount: number
  completeCallCount: number
  failedCallCount: number
  durationSeconds: number
}

export interface OperationsReport {
  monthlyCallCount: number
  monthlyCompleteCallCount: number
  monthlyFailedCallCount: number
  monthlyCompletionRate: number
  monthlyDurationSeconds: number
  averageDurationSeconds: number
  openTodoCount: number
  overdueTodoCount: number
  statusCounts: OperationsStatusCounts
  topLivers: OperationsReportLiverRow[]
  monthlyTrend: OperationsReportMonthPoint[]
  recoveryItems: OperationsRecoveryItem[]
}

export interface OperationsDashboardOptions {
  stuckThresholdMinutes?: number
  dormantThresholdDays?: number
}

export type OperationsHomeItemKind =
  | 'failed_call'
  | 'stuck_call'
  | 'overdue_todo'
  | 'due_follow'
  | 'dormant_liver'
  | 'recent_call'

export type OperationsHomeItemTone = 'red' | 'amber' | 'rose' | 'sky' | 'slate' | 'emerald' | 'violet'

export interface OperationsHomeItem {
  id: string
  kind: OperationsHomeItemKind
  tone: OperationsHomeItemTone
  title: string
  description: string
  liverId: string | null
  liverName: string
  date: string | null
}

export type OperationsTaskDueState = 'overdue' | 'today' | 'upcoming' | 'none'

export type OperationsTaskAssignee = 'manager' | 'liver'
export type OperationsTaskAssigneeFilter = OperationsTaskAssignee | 'all'
export type OperationsTaskDueFilter = OperationsTaskDueState | 'all'

export interface OperationsTaskItem {
  id: string
  callId: string
  liverId: string
  todoIndex: number
  liverName: string
  task: string
  assignee: OperationsTaskAssignee
  dueDate: string | null
  dueState: OperationsTaskDueState
  calledAt: string
  confidence: number | null
  evidence: string | null
}

export interface OperationsTaskFilterOptions {
  assignee?: OperationsTaskAssigneeFilter
  dueState?: OperationsTaskDueFilter
  searchText?: string
}

export interface OperationsRecoveryItem {
  id: string
  callId: string
  liverId: string
  liverName: string
  status: OperationsCallStatus
  calledAt: string
  updatedAt: string | null
  retryable: boolean
  targetStatus: 'pending' | 'awaiting_summary' | null
  reason: string
  errorMessage: string | null
  audioStoragePath: string | null
  transcript: string | null
}

export interface OperationsHomeOptions extends OperationsDashboardOptions {
  limit?: number
  allowStuckRetry?: boolean
}

const DEFAULT_STUCK_THRESHOLD_MINUTES = 30
const DEFAULT_DORMANT_THRESHOLD_DAYS = 14
const STUCK_STATUSES = new Set<OperationsCallStatus>(['transcribing', 'summarizing'])

export function buildOperationsDashboardMetrics(
  calls: OperationsDashboardCall[],
  livers: OperationsDashboardLiver[],
  now: Date,
  options: OperationsDashboardOptions = {}
): OperationsDashboardMetrics {
  const stuckThresholdMs = minutesToMs(
    options.stuckThresholdMinutes ?? DEFAULT_STUCK_THRESHOLD_MINUTES
  )
  const dormantThresholdMs = daysToMs(
    options.dormantThresholdDays ?? DEFAULT_DORMANT_THRESHOLD_DAYS
  )
  const today = startOfLocalDay(now).getTime()

  let monthlyCallCount = 0
  let monthlyDurationSeconds = 0
  let failedCallCount = 0
  let stuckCallCount = 0
  let openTodoCount = 0
  let overdueTodoCount = 0
  let overdueNextFollowCount = 0
  const lastCompleteByLiver = new Map<string, number>()

  for (const call of calls) {
    const calledAt = parseDateTime(call.called_at)

    if (calledAt && isSameLocalMonth(calledAt, now)) {
      monthlyCallCount += 1
      if (isUsableDuration(call.duration_seconds)) {
        monthlyDurationSeconds += call.duration_seconds
      }
    }

    if (call.status === 'failed') {
      failedCallCount += 1
    }

    if (isStuckCall(call, now, stuckThresholdMs)) {
      stuckCallCount += 1
    }

    for (const todo of validTodos(call.todos)) {
      if (todo.completed === true) continue
      openTodoCount += 1

      const dueDate = parseLocalDate(todo.due_date)
      if (dueDate && dueDate.getTime() < today) {
        overdueTodoCount += 1
      }
    }

    const nextFollowDate = parseLocalDate(call.next_follow_date)
    if (nextFollowDate && nextFollowDate.getTime() < today) {
      overdueNextFollowCount += 1
    }

    if (call.status === 'complete' && calledAt) {
      const previous = lastCompleteByLiver.get(call.liver_id)
      if (previous == null || calledAt.getTime() > previous) {
        lastCompleteByLiver.set(call.liver_id, calledAt.getTime())
      }
    }
  }

  let dormantLiverCount = 0
  for (const liver of livers) {
    const lastComplete = lastCompleteByLiver.get(liver.id)
    if (lastComplete == null || now.getTime() - lastComplete >= dormantThresholdMs) {
      dormantLiverCount += 1
    }
  }

  return {
    monthlyCallCount,
    monthlyDurationSeconds,
    failedCallCount,
    stuckCallCount,
    openTodoCount,
    overdueTodoCount,
    overdueNextFollowCount,
    dormantLiverCount
  }
}

export function buildOperationsNextAction(
  metrics: Pick<
    OperationsDashboardMetrics,
    | 'failedCallCount'
    | 'stuckCallCount'
    | 'overdueTodoCount'
    | 'overdueNextFollowCount'
    | 'dormantLiverCount'
    | 'openTodoCount'
  >
): OperationsNextAction {
  if (metrics.failedCallCount > 0) {
    return {
      tone: 'red',
      title: '録音処理の失敗を確認',
      description: `failed が${metrics.failedCallCount}件あります。先に処理復旧で再処理できるか確認してください。`,
      primaryLabel: '処理復旧を見る'
    }
  }
  if (metrics.stuckCallCount > 0) {
    return {
      tone: 'amber',
      title: '処理中のまま止まった通話を確認',
      description: `stuck候補が${metrics.stuckCallCount}件あります。Gateway の状態を見て、必要なら再処理してください。`,
      primaryLabel: 'stuck候補を見る'
    }
  }
  if (metrics.overdueTodoCount > 0) {
    return {
      tone: 'rose',
      title: '期限超過TODOを片付ける',
      description: `期限を過ぎたTODOが${metrics.overdueTodoCount}件あります。今日の対応漏れを先に減らしましょう。`,
      primaryLabel: 'TODOを見る'
    }
  }
  if (metrics.overdueNextFollowCount > 0) {
    return {
      tone: 'sky',
      title: 'フォロー期限を確認',
      description: `フォロー期限を過ぎたライバーが${metrics.overdueNextFollowCount}名います。次回接点を更新してください。`,
      primaryLabel: 'フォローを見る'
    }
  }
  if (metrics.dormantLiverCount > 0) {
    return {
      tone: 'slate',
      title: '休眠候補を1人だけ確認',
      description: `${metrics.dormantLiverCount}名が14日以上通話なしです。まず1人を選んで、LINE履歴か次回フォローを確認してください。`,
      primaryLabel: '休眠候補を見る'
    }
  }
  if (metrics.openTodoCount > 0) {
    return {
      tone: 'violet',
      title: '未完了TODOを整理',
      description: `未完了TODOが${metrics.openTodoCount}件あります。担当がマネージャーのものから確認すると進めやすいです。`,
      primaryLabel: 'TODOを見る'
    }
  }
  return {
    tone: 'emerald',
    title: '急ぎの対応はありません',
    description: '新しい通話やLINE履歴が入ったら、ここに優先対応が表示されます。',
    primaryLabel: 'ライバーを見る'
  }
}

export function buildOperationsReport(
  calls: OperationsDashboardCall[],
  livers: OperationsDashboardLiver[],
  now: Date,
  options: OperationsHomeOptions = {}
): OperationsReport {
  const today = startOfLocalDay(now).getTime()
  const monthKeys = buildRecentMonthKeys(now, 6)
  const monthlyTrend = monthKeys.map((month): OperationsReportMonthPoint => ({
    month,
    callCount: 0,
    completeCallCount: 0,
    failedCallCount: 0,
    durationSeconds: 0
  }))
  const trendByMonth = new Map(monthlyTrend.map((point) => [point.month, point]))
  const liverNameById = new Map(livers.map((liver) => [liver.id, liver.display_name ?? '名称未設定']))
  const liverRows = new Map<string, OperationsReportLiverRow>()

  for (const liver of livers) {
    liverRows.set(liver.id, {
      liverId: liver.id,
      liverName: liver.display_name ?? '名称未設定',
      callCount: 0,
      completeCallCount: 0,
      failedCallCount: 0,
      durationSeconds: 0,
      openTodoCount: 0,
      overdueTodoCount: 0,
      lastCallAt: null
    })
  }

  const statusCounts = emptyStatusCounts()
  let monthlyCallCount = 0
  let monthlyCompleteCallCount = 0
  let monthlyFailedCallCount = 0
  let monthlyDurationSeconds = 0
  let openTodoCount = 0
  let overdueTodoCount = 0

  for (const call of calls) {
    statusCounts[call.status] += 1

    const calledAt = parseDateTime(call.called_at)
    const month = calledAt ? monthKey(calledAt) : null
    const trendPoint = month ? trendByMonth.get(month) : undefined
    if (trendPoint) {
      trendPoint.callCount += 1
      if (call.status === 'complete') trendPoint.completeCallCount += 1
      if (call.status === 'failed') trendPoint.failedCallCount += 1
      if (isUsableDuration(call.duration_seconds)) {
        trendPoint.durationSeconds += call.duration_seconds
      }
    }

    if (!calledAt || !isSameLocalMonth(calledAt, now)) continue

    monthlyCallCount += 1
    if (call.status === 'complete') monthlyCompleteCallCount += 1
    if (call.status === 'failed') monthlyFailedCallCount += 1
    if (isUsableDuration(call.duration_seconds)) {
      monthlyDurationSeconds += call.duration_seconds
    }

    const liverRow = getOrCreateReportLiverRow(liverRows, call.liver_id, liverNameById)
    liverRow.callCount += 1
    if (call.status === 'complete') liverRow.completeCallCount += 1
    if (call.status === 'failed') liverRow.failedCallCount += 1
    if (isUsableDuration(call.duration_seconds)) {
      liverRow.durationSeconds += call.duration_seconds
    }
    if (!liverRow.lastCallAt || compareDateDesc(call.called_at, liverRow.lastCallAt) < 0) {
      liverRow.lastCallAt = call.called_at
    }

    for (const todo of validTodos(call.todos)) {
      if (todo.completed === true) continue
      openTodoCount += 1
      liverRow.openTodoCount += 1
      const dueDate = parseLocalDate(todo.due_date)
      if (dueDate && dueDate.getTime() < today) {
        overdueTodoCount += 1
        liverRow.overdueTodoCount += 1
      }
    }
  }

  return {
    monthlyCallCount,
    monthlyCompleteCallCount,
    monthlyFailedCallCount,
    monthlyCompletionRate: monthlyCallCount > 0 ? monthlyCompleteCallCount / monthlyCallCount : 0,
    monthlyDurationSeconds,
    averageDurationSeconds: monthlyCallCount > 0 ? Math.round(monthlyDurationSeconds / monthlyCallCount) : 0,
    openTodoCount,
    overdueTodoCount,
    statusCounts,
    topLivers: [...liverRows.values()].sort(compareReportLiverRows).slice(0, 8),
    monthlyTrend,
    recoveryItems: buildOperationsRecoveryItems(calls, livers, now, {
      limit: 8,
      allowStuckRetry: options.allowStuckRetry
    })
  }
}

export function buildOperationsRecoveryItems(
  calls: OperationsDashboardCall[],
  livers: OperationsDashboardLiver[],
  now: Date,
  options: OperationsHomeOptions = {}
): OperationsRecoveryItem[] {
  const limit = Math.max(1, Math.trunc(options.limit ?? 20))
  const stuckThresholdMs = minutesToMs(
    options.stuckThresholdMinutes ?? DEFAULT_STUCK_THRESHOLD_MINUTES
  )
  const liverNameById = new Map(livers.map((liver) => [liver.id, liver.display_name ?? '名称未設定']))

  return calls
    .flatMap((call): OperationsRecoveryItem[] => {
      const isFailed = call.status === 'failed'
      const isStuck = isStuckCall(call, now, stuckThresholdMs)
      if (!isFailed && !isStuck) return []
      const recovery = explainCallRecovery(call, options)
      return [{
        id: `recovery:${call.id}`,
        callId: call.id,
        liverId: call.liver_id,
        liverName: liverNameById.get(call.liver_id) ?? '名称未設定',
        status: call.status,
        calledAt: call.called_at,
        updatedAt: call.updated_at ?? null,
        retryable: recovery.retryable,
        targetStatus: recovery.targetStatus,
        reason: recovery.reason,
        errorMessage: call.error_message?.trim() || null,
        audioStoragePath: call.audio_storage_path ?? null,
        transcript: call.transcript ?? null
      }]
    })
    .sort(compareRecoveryItems)
    .slice(0, limit)
}

export function buildOperationsHomeItems(
  calls: OperationsDashboardCall[],
  livers: OperationsDashboardLiver[],
  now: Date,
  options: OperationsHomeOptions = {}
): OperationsHomeItem[] {
  const limit = Math.max(1, Math.trunc(options.limit ?? 8))
  const stuckThresholdMs = minutesToMs(
    options.stuckThresholdMinutes ?? DEFAULT_STUCK_THRESHOLD_MINUTES
  )
  const dormantThresholdMs = daysToMs(
    options.dormantThresholdDays ?? DEFAULT_DORMANT_THRESHOLD_DAYS
  )
  const today = startOfLocalDay(now).getTime()
  const liverNameById = new Map(livers.map((liver) => [liver.id, liver.display_name ?? '名称未設定']))
  const items: OperationsHomeItem[] = []
  const lastCompleteByLiver = new Map<string, number>()
  const sortedCalls = [...calls].sort((a, b) => compareDateDesc(a.called_at, b.called_at))

  for (const call of sortedCalls) {
    const liverName = liverNameById.get(call.liver_id) ?? '名称未設定'
    const calledAt = parseDateTime(call.called_at)

    if (call.status === 'failed') {
      items.push({
        id: `failed:${call.id}`,
        kind: 'failed_call',
        tone: 'red',
        title: '通話処理に失敗',
        description: call.error_message?.trim() || `${liverName} の通話を再確認してください。`,
        liverId: call.liver_id,
        liverName,
        date: call.called_at
      })
    }

    if (isStuckCall(call, now, stuckThresholdMs)) {
      items.push({
        id: `stuck:${call.id}`,
        kind: 'stuck_call',
        tone: 'amber',
        title: '処理が止まっている可能性',
        description: `${liverName} の通話が ${call.status} のままです。`,
        liverId: call.liver_id,
        liverName,
        date: call.updated_at ?? call.called_at
      })
    }

    for (const [index, todo] of validTodos(call.todos).entries()) {
      if (todo.completed === true) continue
      const dueDate = parseLocalDate(todo.due_date)
      if (!dueDate || dueDate.getTime() >= today) continue
      items.push({
        id: `todo:${call.id}:${index}`,
        kind: 'overdue_todo',
        tone: 'rose',
        title: '期限超過TODO',
        description: todo.task?.trim() || `${liverName} のTODOを確認してください。`,
        liverId: call.liver_id,
        liverName,
        date: todo.due_date ?? null
      })
    }

    const nextFollowDate = parseLocalDate(call.next_follow_date)
    if (nextFollowDate && nextFollowDate.getTime() <= today) {
      items.push({
        id: `follow:${call.id}`,
        kind: 'due_follow',
        tone: 'sky',
        title: nextFollowDate.getTime() < today ? 'フォロー期限超過' : '本日フォロー予定',
        description: `${liverName} の次回フォロー日です。`,
        liverId: call.liver_id,
        liverName,
        date: call.next_follow_date
      })
    }

    if (call.status === 'complete' && calledAt) {
      const previous = lastCompleteByLiver.get(call.liver_id)
      if (previous == null || calledAt.getTime() > previous) {
        lastCompleteByLiver.set(call.liver_id, calledAt.getTime())
      }
    }
  }

  for (const liver of livers) {
    if (items.some((item) => item.liverId === liver.id)) continue
    const liverName = liver.display_name ?? '名称未設定'
    const lastComplete = lastCompleteByLiver.get(liver.id)
    if (lastComplete != null && now.getTime() - lastComplete < dormantThresholdMs) continue
    items.push({
      id: `dormant:${liver.id}`,
      kind: 'dormant_liver',
      tone: 'slate',
      title: '14日以上通話なし',
      description:
        lastComplete == null
          ? `${liverName} は直近の完了通話がありません。`
          : `${liverName} は前回通話から14日以上経過しています。`,
      liverId: liver.id,
      liverName,
      date: lastComplete == null ? null : new Date(lastComplete).toISOString()
    })
  }

  for (const call of sortedCalls) {
    if (call.status !== 'complete') continue
    const liverName = liverNameById.get(call.liver_id) ?? '名称未設定'
    items.push({
      id: `recent:${call.id}`,
      kind: 'recent_call',
      tone: 'emerald',
      title: '直近の通話完了',
      description: call.summary?.trim() || `${liverName} の通話が完了しています。`,
      liverId: call.liver_id,
      liverName,
      date: call.called_at
    })
  }

  return items
    .sort((a, b) => priorityForHomeItem(a.kind) - priorityForHomeItem(b.kind))
    .slice(0, limit)
}

export function buildOperationsTaskItems(
  calls: OperationsDashboardCall[],
  livers: OperationsDashboardLiver[],
  now: Date
): OperationsTaskItem[] {
  const today = startOfLocalDay(now).getTime()
  const liverNameById = new Map(livers.map((liver) => [liver.id, liver.display_name ?? '名称未設定']))
  const items: OperationsTaskItem[] = []

  for (const call of calls) {
    const liverName = liverNameById.get(call.liver_id) ?? '名称未設定'
    for (const [index, todo] of (call.todos ?? []).entries()) {
      if (!isValidTodo(todo)) continue
      if (todo.completed === true) continue
      const dueDate = parseLocalDate(todo.due_date)
      items.push({
        id: `task:${call.id}:${index}`,
        callId: call.id,
        liverId: call.liver_id,
        todoIndex: index,
        liverName,
        task: todo.task?.trim() ?? '',
        assignee: normalizeTaskAssignee(todo),
        dueDate: todo.due_date ?? null,
        dueState: dueStateForDate(dueDate, today),
        calledAt: call.called_at,
        confidence: typeof todo.confidence === 'number' && Number.isFinite(todo.confidence)
          ? Math.max(0, Math.min(1, todo.confidence))
          : null,
        evidence: typeof todo.evidence === 'string' && todo.evidence.trim()
          ? todo.evidence.trim()
          : null
      })
    }
  }

  return items.sort(compareTaskItems)
}

export function filterOperationsTaskItems(
  items: OperationsTaskItem[],
  options: OperationsTaskFilterOptions = {}
): OperationsTaskItem[] {
  const assignee = options.assignee ?? 'all'
  const dueState = options.dueState ?? 'all'
  const searchText = options.searchText?.trim().toLocaleLowerCase() ?? ''

  return items.filter((item) => {
    if (assignee !== 'all' && item.assignee !== assignee) return false
    if (dueState !== 'all' && item.dueState !== dueState) return false
    if (!searchText) return true
    return [
      item.task,
      item.liverName,
      item.evidence ?? ''
    ].some((value) => value.toLocaleLowerCase().includes(searchText))
  })
}

export function buildCompletedOperationsTaskTodos(
  call: OperationsDashboardCall,
  item: OperationsTaskItem
): OperationsDashboardTodo[] {
  const todos = (call.todos ?? []).map((todo) => ({ ...todo }))
  if (item.callId !== call.id) return todos

  const target = todos[item.todoIndex]
  if (!target || !isValidTodo(target) || target.task?.trim() !== item.task) {
    return todos
  }

  todos[item.todoIndex] = {
    ...target,
    completed: true
  }
  return todos
}

function isStuckCall(
  call: OperationsDashboardCall,
  now: Date,
  stuckThresholdMs: number
): boolean {
  if (!STUCK_STATUSES.has(call.status)) return false
  const statusAgeStart = parseDateTime(call.updated_at) ?? parseDateTime(call.called_at)
  return statusAgeStart != null && now.getTime() - statusAgeStart.getTime() >= stuckThresholdMs
}

function explainCallRecovery(
  call: OperationsDashboardCall,
  options: OperationsHomeOptions
): Pick<OperationsRecoveryItem, 'retryable' | 'targetStatus' | 'reason'> {
  if (call.status !== 'failed' && options.allowStuckRetry === false) {
    return {
      retryable: false,
      targetStatus: null,
      reason: 'Internal API 未設定のため、stuck 状態の再処理はWebから実行できません。'
    }
  }

  if (call.audio_storage_path?.trim()) {
    return {
      retryable: true,
      targetStatus: 'pending',
      reason: '音声ファイルから文字起こしをやり直せます。'
    }
  }

  if (call.transcript && call.transcript.trim().length >= 20) {
    return {
      retryable: true,
      targetStatus: 'awaiting_summary',
      reason: '文字起こしから要約生成をやり直せます。'
    }
  }

  return {
    retryable: false,
    targetStatus: null,
    reason: '音声ファイルと十分な文字起こしが残っていないため、自動再処理できません。'
  }
}

function compareRecoveryItems(left: OperationsRecoveryItem, right: OperationsRecoveryItem): number {
  if (left.retryable !== right.retryable) return left.retryable ? -1 : 1
  const statusPriority = recoveryStatusPriority(left.status) - recoveryStatusPriority(right.status)
  if (statusPriority !== 0) return statusPriority
  return compareDateDesc(left.updatedAt ?? left.calledAt, right.updatedAt ?? right.calledAt)
}

function recoveryStatusPriority(status: OperationsCallStatus): number {
  if (status === 'failed') return 10
  if (status === 'transcribing' || status === 'summarizing') return 20
  return 30
}

function validTodos(todos: OperationsDashboardTodo[] | null): OperationsDashboardTodo[] {
  return (todos ?? []).filter(isValidTodo)
}

function isValidTodo(todo: OperationsDashboardTodo | null | undefined): todo is OperationsDashboardTodo {
  return typeof todo?.task === 'string' && todo.task.trim().length > 0
}

function normalizeTaskAssignee(todo: OperationsDashboardTodo): OperationsTaskAssignee {
  const assignee = (todo as { assignee?: unknown }).assignee
  return assignee === 'liver' ? 'liver' : 'manager'
}

function dueStateForDate(dueDate: Date | null, today: number): OperationsTaskDueState {
  if (!dueDate) return 'none'
  const dueTime = dueDate.getTime()
  if (dueTime < today) return 'overdue'
  if (dueTime === today) return 'today'
  return 'upcoming'
}

function compareTaskItems(left: OperationsTaskItem, right: OperationsTaskItem): number {
  const priority = taskDuePriority(left.dueState) - taskDuePriority(right.dueState)
  if (priority !== 0) return priority
  const leftDue = parseLocalDate(left.dueDate)?.getTime()
  const rightDue = parseLocalDate(right.dueDate)?.getTime()
  if (leftDue != null && rightDue != null && leftDue !== rightDue) return leftDue - rightDue
  if (leftDue != null && rightDue == null) return -1
  if (leftDue == null && rightDue != null) return 1
  return compareDateDesc(left.calledAt, right.calledAt)
}

function compareReportLiverRows(left: OperationsReportLiverRow, right: OperationsReportLiverRow): number {
  if (left.callCount !== right.callCount) return right.callCount - left.callCount
  if (left.failedCallCount !== right.failedCallCount) return right.failedCallCount - left.failedCallCount
  if (left.openTodoCount !== right.openTodoCount) return right.openTodoCount - left.openTodoCount
  return left.liverName.localeCompare(right.liverName, 'ja')
}

function getOrCreateReportLiverRow(
  rows: Map<string, OperationsReportLiverRow>,
  liverId: string,
  liverNameById: Map<string, string>
): OperationsReportLiverRow {
  const existing = rows.get(liverId)
  if (existing) return existing
  const created = {
    liverId,
    liverName: liverNameById.get(liverId) ?? '名称未設定',
    callCount: 0,
    completeCallCount: 0,
    failedCallCount: 0,
    durationSeconds: 0,
    openTodoCount: 0,
    overdueTodoCount: 0,
    lastCallAt: null
  }
  rows.set(liverId, created)
  return created
}

function emptyStatusCounts(): OperationsStatusCounts {
  return {
    pending: 0,
    recording: 0,
    transcribing: 0,
    awaiting_summary: 0,
    summarizing: 0,
    complete: 0,
    failed: 0
  }
}

function buildRecentMonthKeys(now: Date, count: number): string[] {
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (count - 1 - index), 1)
    return monthKey(date)
  })
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function taskDuePriority(state: OperationsTaskDueState): number {
  switch (state) {
    case 'overdue':
      return 10
    case 'today':
      return 20
    case 'upcoming':
      return 30
    case 'none':
      return 40
  }
}

function isUsableDuration(value: number | null): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

function isSameLocalMonth(date: Date, now: Date): boolean {
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()
}

function parseDateTime(value: string | null | undefined): Date | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function parseLocalDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(year, month - 1, day)
  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date
    : null
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function compareDateDesc(left: string | null | undefined, right: string | null | undefined): number {
  return (parseDateTime(right)?.getTime() ?? 0) - (parseDateTime(left)?.getTime() ?? 0)
}

function priorityForHomeItem(kind: OperationsHomeItemKind): number {
  switch (kind) {
    case 'failed_call':
      return 10
    case 'stuck_call':
      return 20
    case 'overdue_todo':
      return 30
    case 'due_follow':
      return 40
    case 'dormant_liver':
      return 50
    case 'recent_call':
      return 60
  }
}

function minutesToMs(minutes: number): number {
  return Math.max(0, minutes) * 60 * 1000
}

function daysToMs(days: number): number {
  return Math.max(0, days) * 24 * 60 * 60 * 1000
}
